"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import cn from "classnames";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Round copy-to-clipboard control that sits in the action row under an
 * assistant message, styled to match the thumbs up/down in
 * AIChatFeedbackButtons. Shared by the Sabine chat panel, the playbook chat
 * and the home-page Sabine thread.
 */
export default function AIChatCopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("handleCopy: clipboard write failed", err);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition duration-150 motion-safe:hover:scale-110 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
            )}
            aria-label="Copy message"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied!" : "Copy message"}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
